// socketService.js - Versão compatível com Socket.IO v2
import io from 'socket.io-client';
import { getToken } from '../store/auth'

class SocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectInterval = 2000;
    this.eventCallbacks = new Map();
  }

  connect(url, options = {}) {
    return new Promise((resolve, reject) => {
      try {
        // Verificar se io está disponível
        if (typeof io === 'undefined') {
          reject(new Error('Socket.IO client não está disponível'));
          return;
        }

        const token = getToken();
        
        this.socket = io(url, {
          ...options,
          query: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectInterval,
          reconnectionDelayMax: 10000,
          timeout: 10000,
          forceNew: true,
          upgrade: true,
          rememberUpgrade: true
        });

        this.socket.on('connect', () => {
          console.log('Socket conectado com sucesso');
          this.reconnectAttempts = 0;
          resolve(this.socket);
        });

        this.socket.on('connect_error', (error) => {
          console.error('Erro de conexão do socket:', error);
          reject(error);
        });

        this.socket.on('disconnect', (reason) => {
          console.log('Socket desconectado:', reason);
          this.executeCallbacks('disconnect', reason);
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`Socket reconectado na tentativa ${attemptNumber}`);
          this.executeCallbacks('reconnect', attemptNumber);
        });

        this.socket.on('reconnecting', (attemptNumber) => {
          console.log(`Tentativa de reconexão ${attemptNumber}`);
          this.reconnectAttempts = attemptNumber;
          this.executeCallbacks('reconnecting', attemptNumber);
        });

        this.socket.on('reconnect_error', (error) => {
          console.error('Erro na reconexão:', error);
          this.executeCallbacks('reconnect_error', error);
        });

        this.socket.on('reconnect_failed', () => {
          console.error('Falha na reconexão após todas as tentativas');
          this.executeCallbacks('reconnect_failed');
        });

        // Registrar todos os callbacks previamente adicionados
        this.registerExistingCallbacks();

      } catch (error) {
        console.error('Erro ao conectar socket:', error);
        reject(error);
      }
    });
  }

  registerExistingCallbacks() {
    this.eventCallbacks.forEach((callbacks, event) => {
      callbacks.forEach(callback => {
        this.socket.on(event, callback);
      });
    });
  }

  on(event, callback) {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
    }
    this.eventCallbacks.get(event).push(callback);
    
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback = null) {
    if (callback) {
      if (this.eventCallbacks.has(event)) {
        const callbacks = this.eventCallbacks.get(event);
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
      
      if (this.socket) {
        this.socket.off(event, callback);
      }
    } else {
      this.eventCallbacks.delete(event);
      
      if (this.socket) {
        this.socket.off(event);
      }
    }
  }

  emit(event, data, callback) {
    if (this.socket && this.socket.connected) {
      return this.socket.emit(event, data, callback);
    } else {
      console.warn('Socket não conectado, não é possível emitir evento:', event);
      
      if (callback) {
        callback(new Error('Socket não conectado'));
      }
      
      return false;
    }
  }

  executeCallbacks(event, data) {
    if (this.eventCallbacks.has(event)) {
      // Usar cópia para evitar problemas durante execução
      const callbacks = [...this.eventCallbacks.get(event)];
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Erro ao executar callback para evento ${event}:`, error);
        }
      });
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.eventCallbacks.clear();
  }

  get isConnected() {
    return this.socket && this.socket.connected;
  }

  get id() {
    return this.socket?.id;
  }

  reconnect() {
    if (this.socket) {
      this.socket.connect();
    }
  }
}

// Instância singleton
export const socketServiceInstance = new SocketService();
export default socketServiceInstance;